import { useEffect, useRef, useState } from "react";

/**
 * Native-feel pull-to-refresh for mobile. Attaches touch listeners to the
 * document; only triggers when the page is already scrolled to top and the
 * pointer is a touch (not a mouse). Returns { pulling, distance, isRefreshing }
 * plus a THRESHOLD constant so the caller can size the spinner.
 *
 * @param {() => Promise<any> | void} onRefresh
 * @param {{ threshold?: number, maxPull?: number, disabled?: boolean }} opts
 */
export default function usePullToRefresh(onRefresh, opts = {}) {
  const threshold = opts.threshold ?? 70;
  const maxPull = opts.maxPull ?? 110;
  const disabled = !!opts.disabled;
  const [distance, setDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef(null);
  const pullingRef = useRef(false);

  useEffect(() => {
    if (disabled) return undefined;

    const onTouchStart = (e) => {
      if (isRefreshing) return;
      if (window.scrollY > 0) return;
      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      pullingRef.current = false;
    };

    const onTouchMove = (e) => {
      if (isRefreshing || startYRef.current == null) return;
      const y = e.touches[0].clientY;
      const dy = y - startYRef.current;
      if (dy > 0 && window.scrollY === 0) {
        pullingRef.current = true;
        // Rubber-band feel
        const eased = Math.min(maxPull, dy * 0.5);
        setDistance(eased);
      } else if (dy <= 0) {
        setDistance(0);
      }
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) {
        startYRef.current = null;
        return;
      }
      const reached = distance >= threshold;
      pullingRef.current = false;
      startYRef.current = null;
      if (reached) {
        setIsRefreshing(true);
        setDistance(threshold);
        try { await onRefresh?.(); } finally {
          setIsRefreshing(false);
          setDistance(0);
        }
      } else {
        setDistance(0);
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh, threshold, maxPull, disabled, distance, isRefreshing]);

  return { distance, isRefreshing, threshold };
}
